import React, { useState } from 'react';
import axios from 'axios';

const MobileCalendarView = ({ events, setEvents, tasks, user, adminUsers, API_BASE }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_date: new Date().toISOString().split('T')[0],
    event_time: '',
    end_time: ''
  });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const formatDate = (day) => {
    if (!day) return null;
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const getEventsForDate = (day) => {
    if (!day) return [];
    const dateStr = formatDate(day);
    return events.filter(event => event.event_date?.substring(0, 10) === dateStr);
  };

  const getTasksForDate = (day) => {
    if (!day) return [];
    const dateStr = formatDate(day);
    return tasks.filter(task => task.task_date?.substring(0, 10) === dateStr);
  };

  const hasItems = (day) => {
    return getEventsForDate(day).length > 0 || getTasksForDate(day).length > 0;
  };

  const handleDayClick = (day) => {
    if (!day) return;
    setSelectedDate(day);
  };

  const formatEventTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title.trim()) {
      alert('Event title is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/calendar/events`,
        eventForm,
        { withCredentials: true }
      );
      setEvents([...events, response.data]);
      setEventForm({
        title: '',
        description: '',
        event_date: new Date().toISOString().split('T')[0],
        event_time: '',
        end_time: ''
      });
      setShowEventModal(false);
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event');
    }
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];
  const selectedDateTasks = selectedDate ? getTasksForDate(selectedDate) : [];

  return (
    <div className="mobile-calendar-view">
      {/* Header */}
      <div className="mobile-module-header">
        <div className="module-header-left">
          <div className="module-icon calendar">📅</div>
          <span className="module-title">Calendar</span>
        </div>
        <button className="add-btn" onClick={() => setShowEventModal(true)}>+</button>
      </div>

      {/* Calendar Grid */}
      <div style={{ padding: '1rem', background: 'white' }}>
        {/* Month Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <button
            onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
            style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }}
          >
            ‹
          </button>
          <span style={{ fontWeight: 600, color: '#2d3748' }}>{monthNames[month]} {year}</span>
          <button
            onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
            style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }}
          >
            ›
          </button>
        </div>

        {/* Day Names */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '0.5rem' }}>
          {dayNames.map((day, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: '0.7rem', color: '#a0aec0', fontWeight: 500 }}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {days.map((day, index) => (
            <div
              key={index}
              onClick={() => handleDayClick(day)}
              style={{
                aspectRatio: '1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                cursor: day ? 'pointer' : 'default',
                background: isToday(day) ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : selectedDate === day ? '#f0f5ff' : 'transparent',
                color: isToday(day) ? 'white' : '#4a5568',
                fontWeight: isToday(day) ? 600 : 400,
                fontSize: '0.85rem',
                position: 'relative'
              }}
            >
              {day}
              {hasItems(day) && (
                <div style={{
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: isToday(day) ? 'white' : '#ed8936',
                  marginTop: '2px'
                }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div style={{ padding: '1rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2d3748', marginBottom: '0.75rem' }}>
            {new Date(year, month, selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </h3>

          {selectedDateEvents.length === 0 && selectedDateTasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#a0aec0', background: 'white', borderRadius: '12px' }}>
              No events or tasks for this day
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {selectedDateEvents.map(event => (
                <div key={event.id} style={{ background: 'white', borderRadius: '12px', padding: '1rem', borderLeft: '3px solid #9f7aea' }}>
                  <div style={{ fontWeight: 500, color: '#2d3748' }}>{event.title}</div>
                  {event.event_time && (
                    <div style={{ fontSize: '0.8rem', color: '#718096' }}>
                      {formatEventTime(event.event_time)}
                      {event.end_time && ` - ${formatEventTime(event.end_time)}`}
                    </div>
                  )}
                </div>
              ))}
              {selectedDateTasks.map(task => (
                <div key={task.id} style={{ background: 'white', borderRadius: '12px', padding: '1rem', borderLeft: '3px solid #4299e1' }}>
                  <div style={{ fontWeight: 500, color: '#2d3748' }}>{task.title}</div>
                  <div style={{ fontSize: '0.8rem', color: '#718096' }}>Task • {task.priority} priority</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Event Creation Modal */}
      {showEventModal && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Event</h3>
              <button className="modal-close" onClick={() => setShowEventModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Event Title *</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder="Enter event title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Enter event description"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Event Date</label>
                <input
                  type="date"
                  value={eventForm.event_date}
                  onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={eventForm.event_time}
                    onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={eventForm.end_time}
                    onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEventModal(false)}>Cancel</button>
              <button className="btn-create" onClick={handleCreateEvent}>Create Event</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileCalendarView;
